import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment-timezone';
import { Commons } from 'components';
import { KimAndChangStatsList } from 'components/SikdaeUseStats';
import * as cmAction from 'actions/commons';
import * as usageAction from 'actions/usage';
import cm from 'helpers/commons';
import toastr from 'helpers/toastr';
import { DeductionSearchBox } from 'components/SikdaeUseStats';
import { KimAndChangCompanySupportRate } from 'constants/enum';

moment.tz.setDefault('Asia/Seoul');

const DeductionSikdaeKimAndChang = ({ dateType, date, dateCheck, usageStat, cmAPI, usageAPI }) => {
  // 페이지 진입시 통계 데이터 초기화
  React.useEffect(() => {
    usageAPI.initializeUsageStats();
  }, []);
  const [state, setState] = React.useState({
    page: 1,
    pageRow: 15,
    totalCount: null,
    allData: null, // 전체 데이터 저장
    staffModal: {
      open: false,
      size: 'large'
    },
    staff: {
      name: '',
      id: ''
    },
    dimmer: false,
    isLoading: false,
    companySupportRate: KimAndChangCompanySupportRate.SEVENTY_SIX_POINT_SEVEN_PERCENT // 기본값: 76.7% 지원
  });

  React.useEffect(() => {
    cm.mainTitle('김앤장 급여공제');
    cm.propsInit({ dateType, date });
  }, []);

  const modalAction = React.useCallback((type, dimmer) => {
    if (type === 'open') {
      staffSel(1, null);
    }

    setState((prev) => ({
      ...prev,
      dimmer,
      staffModal: { ...prev.staffModal, open: dimmer }
    }));
  }, []);

  const staffSel = async (page, keyword) => {
    try {
      await cmAPI.staffSel({ page, pagerow: 10, keyword });
    } catch (e) {
      console.error('Staff selection error:', e);
    }
  };

  const choiceStaff = React.useCallback((data) => {
    setState((prev) => ({
      ...prev,
      staff: {
        name: data ? data.staff.name : '',
        id: data ? data.staff.id : ''
      },
      staffModal: { ...prev.staffModal, open: false }
    }));
  }, []);

  // 회사지원비율 변경 함수
  const changeCompanySupportRate = React.useCallback((rate) => {
    setState((prev) => ({
      ...prev,
      companySupportRate: rate
    }));
  }, []);

  const submitForm = async (e) => {
    e.preventDefault();

    if (!dateCheck.isValue.isState) {
      toastr('top-right', 'warning', dateCheck.isValue.message);
      return;
    }

    setState((prev) => ({ ...prev, page: 1, isLoading: true }));
    deductionUsageStatList();

    cmAPI.changeExcel({
      excel: {
        startDate: date?.sDate,
        endDate: date?.eDate,
        page: 1,
        pagerow: state.pageRow,
        userId: state.staff.id || null,
        companySupportRate: state.companySupportRate // 기본값: 76.7% 지원
      }
    });
  };

  const deductionUsageStatList = async (pageNum = 1) => {
    try {
      const params = {
        startDate: date
          ? date.sDate
          : moment()
              .startOf('month')
              .format('LLL'),
        endDate: date ? date.eDate : moment().format('LLL'),
        userId: state.staff.id === '' ? null : state.staff.id,
        companySupportRate: state.companySupportRate
      };

      await usageAPI.usageStatList(params);

      if (usageStat?.data?.stat) {
        setState((prev) => ({
          ...prev,
          allData: usageStat.data,
          totalCount: usageStat.data.stat.length,
          isLoading: false
        }));
      } else {
        setState((prev) => ({
          ...prev,
          allData: null,
          totalCount: 0,
          isLoading: false
        }));
      }

      cmAPI.changeExcel({
        excel: params
      });
    } catch (error) {
      console.error('Error fetching deduction stats:', error);
      setState((prev) => ({ ...prev, isLoading: false }));

      // API 에러 응답 처리
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || '조회 중 오류가 발생했습니다.';
        toastr('top-center', 'error', errorMessage);
      } else {
        toastr('top-center', 'error', '데이터 조회 중 오류가 발생했습니다.');
      }
    }
  };

  // 페이지 변경시 저장된 전체 데이터에서 해당 페이지 데이터만 추출
  const pageClick = React.useCallback((page) => {
    setState((prev) => ({ ...prev, page }));
  }, []);

  const { page, pageRow, staffModal, dimmer, staff, companySupportRate } = state;

  const modal = {
    open: staffModal.open,
    size: staffModal.size,
    dimmer,
    close: modalAction
  };

  return (
    <main className="sus">
      {state.isLoading && <Commons.LoadingBar />}
      <DeductionSearchBox
        modalAction={modalAction}
        staff={staff}
        fn={{ choiceStaff }}
        submitForm={submitForm}
        defaultDay={{ startDay: 26, endDay: 25 }} // 김앤장 급여공제 기간 설정 (26일 ~ 25일)
        maxDays={62} // 김앤장 급여공제 조회 기간 제한 (62일)
        companySupportRate={companySupportRate}
        onCompanySupportRateChange={changeCompanySupportRate}
        isKimAndChang={true} // 김앤장 급여공제 화면임을 표시
      />

      <KimAndChangStatsList
        listFn={{
          page,
          pageRow,
          totalCount: usageStat?.data?.paging?.totalcount || 0,
          pageClick
        }}
        usageStat={usageStat}
      />

      {staffModal.open && <Commons.StaffSearchModal modal={modal} staffSel={staffSel} choiceEvent={choiceStaff} />}
    </main>
  );
};

export default connect(
  (state) => ({
    dateType: state.commons.dateType,
    date: state.commons.date,
    dateCheck: state.commons.dateCheck,
    usageStat: state.usage.usageStats
  }),
  (dispatch) => ({
    cmAPI: bindActionCreators(
      {
        staffSel: cmAction.StaffSel,
        changeExcel: cmAction.ChangeExcel
      },
      dispatch
    ),
    usageAPI: bindActionCreators(
      {
        usageStatList: usageAction.DeductionKimAndChangUsageStatsList,
        initializeUsageStats: usageAction.initializeUsageStats
      },
      dispatch
    )
  })
)(DeductionSikdaeKimAndChang);
